import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
    Users, UserPlus, UserMinus, ChatDots, Path, Trophy,
    MagnifyingGlass, Lightning
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const getH = () => {
    const t = localStorage.getItem('btr_session_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function Social() {
    const { user } = useAuth();
    const [feed, setFeed] = useState([]);
    const [following, setFollowing] = useState([]);
    const [followers, setFollowers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchId, setSearchId] = useState('');

    useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchAll = async () => {
        try {
            const [feedRes, followingRes, followersRes] = await Promise.all([
                fetch(`${API}/social/feed`, { credentials: 'include', headers: getH() }),
                fetch(`${API}/social/following`, { credentials: 'include', headers: getH() }),
                fetch(`${API}/social/followers`, { credentials: 'include', headers: getH() }),
            ]);
            if (feedRes.ok) setFeed(await feedRes.json());
            if (followingRes.ok) setFollowing(await followingRes.json());
            if (followersRes.ok) setFollowers(await followersRes.json());
        } catch (e) { console.error('Social feed error:', e); } finally { setLoading(false); }
    };

    const followUser = async (userId) => {
        const res = await fetch(`${API}/social/follow/${userId}`, { method: 'POST', credentials: 'include', headers: getH() });
        if (res.ok) { toast.success('Following!'); fetchAll(); }
        else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    };

    const unfollowUser = async (userId) => {
        const res = await fetch(`${API}/social/follow/${userId}`, { method: 'DELETE', credentials: 'include', headers: getH() });
        if (res.ok) { toast.success('Unfollowed'); fetchAll(); }
    };

    const isFollowing = (userId) => following.some(f => f.user_id === userId);

    const formatTime = (ts) => {
        if (!ts) return '';
        const d = new Date(ts);
        const now = new Date();
        const diff = Math.floor((now - d) / 60000);
        if (diff < 60) return `${diff}m ago`;
        if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
        return `${Math.floor(diff / 1440)}d ago`;
    };

    return (
        <Layout>
            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
                    <span className="label-text text-plasma-blue text-glow-blue">COMMUNITY</span>
                    <h1 className="text-2xl sm:text-3xl font-bold mt-1 font-display text-foreground">Social Hub</h1>
                </motion.div>

                <Tabs defaultValue="feed" className="w-full">
                    <TabsList className="grid w-full grid-cols-3 mb-6 h-auto">
                        <TabsTrigger value="feed" className="py-2 text-xs"><Lightning size={14} className="mr-1" />Feed</TabsTrigger>
                        <TabsTrigger value="following" className="py-2 text-xs"><Users size={14} className="mr-1" />Following ({following.length})</TabsTrigger>
                        <TabsTrigger value="followers" className="py-2 text-xs"><Users size={14} className="mr-1" />Followers ({followers.length})</TabsTrigger>
                    </TabsList>

                    {/* Feed */}
                    <TabsContent value="feed">
                        {loading ? (
                            <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}</div>
                        ) : feed.length === 0 ? (
                            <Card className="glass-card text-center py-12">
                                <Users size={48} className="text-muted-foreground mx-auto mb-3" />
                                <p className="text-muted-foreground font-display">No activity yet</p>
                                <p className="text-sm text-muted-foreground mt-1">Follow riders to see their activity here</p>
                            </Card>
                        ) : (
                            <div className="space-y-3">
                                {feed.map((item, i) => (
                                    <motion.div key={item.post_id || item.ride_id || `feed-${i}`} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                                        <Card className="glass-card">
                                            <CardContent className="p-4 flex items-center gap-3">
                                                <Avatar className="w-10 h-10 shrink-0">
                                                    <AvatarImage src={item.user?.picture} />
                                                    <AvatarFallback className="bg-plasma-red/20 text-plasma-red text-xs font-bold">
                                                        {item.user?.name?.charAt(0) || '?'}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-bold truncate">{item.user?.name || 'Rider'}</p>
                                                    {item.type === 'ride' ? (
                                                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                            <Path size={12} className="text-plasma-blue" />
                                                            Rode {item.data?.distance?.toFixed(1)}km
                                                            <span className="text-boltz-gold ml-1">+{item.data?.boltz_earned?.toFixed(1)} Boltz</span>
                                                        </p>
                                                    ) : item.type === 'achievement' ? (
                                                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                            <Trophy size={12} className="text-boltz-gold" />
                                                            Unlocked <span className="text-foreground font-medium">{item.data?.title}</span>
                                                            <Badge className="text-[9px] ml-1">{item.data?.rarity}</Badge>
                                                        </p>
                                                    ) : null}
                                                </div>
                                                <span className="text-[10px] text-muted-foreground shrink-0">{formatTime(item.timestamp)}</span>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* Following */}
                    <TabsContent value="following">
                        {following.length === 0 ? (
                            <Card className="glass-card text-center py-12">
                                <UserPlus size={48} className="text-muted-foreground mx-auto mb-3" />
                                <p className="text-muted-foreground">Not following anyone yet</p>
                            </Card>
                        ) : (
                            <div className="space-y-2">
                                {following.map(u => (
                                    <Card key={u.user_id} className="glass-card">
                                        <CardContent className="p-3 flex items-center gap-3">
                                            <Avatar className="w-10 h-10">
                                                <AvatarImage src={u.picture} />
                                                <AvatarFallback className="bg-plasma-blue/20 text-plasma-blue text-xs">{u.name?.charAt(0) || '?'}</AvatarFallback>
                                            </Avatar>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-bold truncate">{u.name || u.email}</p>
                                                <p className="text-xs text-muted-foreground">{u.volt_tier} | {u.total_distance?.toFixed(1)}km</p>
                                            </div>
                                            <Button size="sm" variant="outline" className="text-xs border-destructive/30 text-destructive" onClick={() => unfollowUser(u.user_id)}>
                                                <UserMinus size={14} className="mr-1" />Unfollow
                                            </Button>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* Followers */}
                    <TabsContent value="followers">
                        {followers.length === 0 ? (
                            <Card className="glass-card text-center py-12">
                                <Users size={48} className="text-muted-foreground mx-auto mb-3" />
                                <p className="text-muted-foreground">No followers yet</p>
                            </Card>
                        ) : (
                            <div className="space-y-2">
                                {followers.map(u => (
                                    <Card key={u.user_id} className="glass-card">
                                        <CardContent className="p-3 flex items-center gap-3">
                                            <Avatar className="w-10 h-10">
                                                <AvatarImage src={u.picture} />
                                                <AvatarFallback className="bg-neon-green/20 text-neon-green text-xs">{u.name?.charAt(0) || '?'}</AvatarFallback>
                                            </Avatar>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-bold truncate">{u.name || 'Rider'}</p>
                                                <p className="text-xs text-muted-foreground">Lv.{u.level || 1}</p>
                                            </div>
                                            {!isFollowing(u.user_id) && (
                                                <Button size="sm" className="text-xs bg-plasma-blue text-white" onClick={() => followUser(u.user_id)}>
                                                    <UserPlus size={14} className="mr-1" />Follow
                                                </Button>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </Layout>
    );
}
